import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import CareerDetailModal from '../components/CareerDetailModal';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Wallet, GraduationCap, Clock, MapPin, Star, 
  Briefcase, Scale, ChevronRight, Sparkles, Trophy, X, Download, FileText, Info
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Domain colors
const domainColors = {
  social: '#E91E63', education: '#9C27B0', health: '#F44336',
  creativity: '#FF9800', technology: '#2196F3', technical: '#607D8B',
  science: '#4CAF50', business: '#673AB7', administration: '#795548',
  sports: '#00BCD4'
};

export default function CompareCarers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  
  const [careers, setCareers] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [selectedCareerForModal, setSelectedCareerForModal] = useState(null);
  
  const texts = {
    fr: {
      title: "Comparaison de métiers",
      subtitle: "Compare les métiers côte à côte pour faire le meilleur choix",
      salary: "Salaire indicatif",
      studies: "Formation requise",
      typicalDay: "Journée type",
      workEnv: "Environnement",
      skills: "Compétences",
      domains: "Domaines",
      description: "Description",
      noSelection: "Aucun métier sélectionné",
      selectCareers: "Sélectionne des métiers à comparer depuis la page Explorer",
      goToExplore: "Explorer les métiers",
      interested: "En savoir plus",
      backToExplore: "Retour à l'exploration",
      addMore: "Ajouter un métier",
      remove: "Retirer",
      bestFor: "Idéal si tu cherches",
      notAvailable: "Non renseigné",
      downloadPdf: "Télécharger en PDF",
      downloading: "Téléchargement..."
    },
    nl: {
      title: "Beroepen vergelijken",
      subtitle: "Vergelijk beroepen naast elkaar om de beste keuze te maken",
      salary: "Indicatief salaris",
      studies: "Vereiste opleiding",
      typicalDay: "Typische dag",
      workEnv: "Werkomgeving",
      skills: "Vaardigheden",
      domains: "Domeinen",
      description: "Beschrijving",
      noSelection: "Geen beroepen geselecteerd",
      selectCareers: "Selecteer beroepen om te vergelijken op de Verkennen pagina",
      goToExplore: "Verken beroepen",
      interested: "Meer informatie",
      backToExplore: "Terug naar verkennen",
      addMore: "Beroep toevoegen",
      remove: "Verwijderen",
      bestFor: "Ideaal als je zoekt naar",
      notAvailable: "Niet beschikbaar",
      downloadPdf: "Download als PDF",
      downloading: "Downloaden..."
    }
  };
  
  const txt = texts[language] || texts.fr;
  
  useEffect(() => {
    // Get careers from navigation state (support both naming conventions)
    const selectedCareers = location.state?.careers || location.state?.selectedCareers || [];
    setCareers(selectedCareers);
  }, [location.state]);
  
  const removeCareer = (careerId) => {
    setCareers(prev => prev.filter(c => c.id !== careerId));
  };
  
  // Prepare career data for the modal (adapt field names)
  const prepareCareerForModal = (career) => {
    return {
      id: career.id,
      name: career.name || career.title,
      title: career.name || career.title,
      description: career.description || '',
      salary_range: career.salary_range || career.salary || '',
      studies: career.studies || career.formation || '',
      typical_day: career.typical_day || career.typicalDay || '',
      work_environment: career.work_environment || career.workEnv || career.environment || '',
      skills: career.skills || [],
      domains: career.domains || [],
      match: career.match || (career.score ? `${career.score}%` : null)
    };
  };
  
  const downloadPdf = async () => {
    if (careers.length < 2) return;
    
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/export/compare-pdf`,
        { careers, language },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', language === 'fr' ? 'comparaison-metiers-declic.pdf' : 'beroepenvergelijking-declic.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
    } finally {
      setDownloading(false);
    }
  };
  
  // If no careers selected, show empty state
  if (careers.length === 0) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-[#F3E5F5] rounded-full flex items-center justify-center">
              <Scale className="w-10 h-10 text-[#A4187A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1D1D1D] mb-3">{txt.noSelection}</h1>
            <p className="text-gray-600 mb-6">{txt.selectCareers}</p>
            <button
              onClick={() => navigate('/explore')}
              className="neo-button-primary"
            >
              {txt.goToExplore}
            </button>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Get best salary (for highlighting)
  const salaries = careers.map(c => {
    const match = c.salary_range?.match(/[\d.]+/g);
    return match ? parseFloat(match[match.length - 1].replace('.', '')) : 0;
  });
  const maxSalaryIdx = salaries.indexOf(Math.max(...salaries));
  
  // Get shortest studies (for highlighting)
  const studiesYears = careers.map(c => {
    const match = c.studies?.match(/(\d+)\s*ans?/);
    return match ? parseInt(match[1]) : 99;
  });
  const minStudiesIdx = studiesYears.indexOf(Math.min(...studiesYears));
  
  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      <main className="flex-1 px-4 md:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#A4187A]/10 rounded-full mb-4">
              <Scale className="w-5 h-5 text-[#A4187A]" />
              <span className="text-sm font-bold text-[#A4187A]">{careers.length} métiers</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-[#A4187A] mb-2">
              {txt.title}
            </h1>
            <p className="text-gray-600">{txt.subtitle}</p>
          </motion.div>
          
          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center justify-between gap-4 mb-6"
          >
            {/* Back button */}
            <button
              onClick={() => navigate('/explore', { state: { selectedForCompare: careers } })}
              className="flex items-center gap-2 text-[#A4187A] font-medium hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              {txt.backToExplore}
            </button>
            
            {/* Download PDF button */}
            <button
              onClick={downloadPdf}
              disabled={downloading || careers.length < 2}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md ${
                downloading 
                  ? 'bg-gray-300 text-gray-500 cursor-wait'
                  : 'bg-gradient-to-r from-[#A4187A] to-[#FB6823] text-white hover:opacity-90'
              }`}
              data-testid="download-compare-pdf-btn"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {txt.downloading}
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  {txt.downloadPdf}
                </>
              )}
            </button>
          </motion.div>
          
          {/* Comparison Grid */}
          <div className="overflow-x-auto pb-4">
            <div className={`grid gap-4 min-w-[800px]`} style={{ gridTemplateColumns: `repeat(${careers.length}, minmax(280px, 1fr))` }}>
              
              {careers.map((career, idx) => (
                <motion.div
                  key={career.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-100 hover:border-[#A4187A]/30 transition-colors"
                >
                  {/* Career Header */}
                  <div className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] p-4 text-white relative">
                    <button
                      onClick={() => removeCareer(career.id)}
                      className="absolute top-2 right-2 p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      title={txt.remove}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    
                    {idx === 0 && (
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold">
                        <Trophy className="w-3 h-3" />
                        #1
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3 mt-4">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <Briefcase className="w-5 h-5" />
                      </div>
                      <h2 className="text-lg font-bold pr-6">{career.title}</h2>
                    </div>
                    
                    {/* Domain badges */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {career.domains?.slice(0, 3).map((domain, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full"
                        >
                          {t(`domains.${domain}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Career Details */}
                  <div className="p-4 space-y-4">
                    
                    {/* Description */}
                    <div>
                      <p className="text-sm text-gray-600 line-clamp-3">
                        {career.description}
                      </p>
                    </div>
                    
                    {/* Salary */}
                    <div className={`p-3 rounded-xl ${idx === maxSalaryIdx ? 'bg-green-100 ring-2 ring-green-400' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Wallet className={`w-4 h-4 ${idx === maxSalaryIdx ? 'text-green-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium text-gray-500">{txt.salary}</span>
                        {idx === maxSalaryIdx && (
                          <span className="ml-auto text-xs font-bold text-green-600 bg-green-200 px-2 py-0.5 rounded-full">
                            💰 Max
                          </span>
                        )}
                      </div>
                      <p className={`font-bold ${idx === maxSalaryIdx ? 'text-green-700' : 'text-gray-700'}`}>
                        {career.salary_range || txt.notAvailable}
                      </p>
                    </div>
                    
                    {/* Studies */}
                    <div className={`p-3 rounded-xl ${idx === minStudiesIdx ? 'bg-blue-100 ring-2 ring-blue-400' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <GraduationCap className={`w-4 h-4 ${idx === minStudiesIdx ? 'text-blue-600' : 'text-gray-500'}`} />
                        <span className="text-xs font-medium text-gray-500">{txt.studies}</span>
                        {idx === minStudiesIdx && (
                          <span className="ml-auto text-xs font-bold text-blue-600 bg-blue-200 px-2 py-0.5 rounded-full">
                            ⚡ Rapide
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${idx === minStudiesIdx ? 'text-blue-700 font-semibold' : 'text-gray-700'}`}>
                        {career.studies || txt.notAvailable}
                      </p>
                    </div>
                    
                    {/* Typical Day */}
                    <div className="p-3 bg-orange-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-orange-500" />
                        <span className="text-xs font-medium text-gray-500">{txt.typicalDay}</span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-3">
                        {career.typical_day || txt.notAvailable}
                      </p>
                    </div>
                    
                    {/* Work Environment */}
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-500">{txt.workEnv}</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        {career.work_environment || txt.notAvailable}
                      </p>
                    </div>
                    
                    {/* Skills */}
                    <div className="p-3 bg-purple-50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-[#A4187A]" />
                        <span className="text-xs font-medium text-gray-500">{txt.skills}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {career.skills?.slice(0, 4).map((skill, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-white text-[#A4187A] rounded text-xs font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <button
                      onClick={() => setSelectedCareerForModal(prepareCareerForModal(career))}
                      className="w-full py-3 bg-gradient-to-r from-[#A4187A] to-[#FB6823] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <Info className="w-4 h-4" />
                      {txt.interested}
                    </button>
                  </div>
                </motion.div>
              ))}
              
              {/* Add More Card (if less than 3) */}
              {careers.length < 3 && (
                <motion.button
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: careers.length * 0.1 }}
                  onClick={() => navigate('/explore', { state: { selectedForCompare: careers } })}
                  className="min-h-[600px] bg-white/50 border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-4 hover:border-[#A4187A] hover:bg-[#F3E5F5]/30 transition-colors cursor-pointer"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-3xl text-gray-400">+</span>
                  </div>
                  <span className="text-gray-500 font-medium">{txt.addMore}</span>
                </motion.button>
              )}
            </div>
          </div>
          
          {/* Legend */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 flex flex-wrap justify-center gap-4 text-sm text-gray-500"
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-400 rounded"></div>
              <span>{language === 'fr' ? 'Meilleur salaire' : 'Hoogste salaris'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-400 rounded"></div>
              <span>{language === 'fr' ? 'Formation la plus courte' : 'Kortste opleiding'}</span>
            </div>
          </motion.div>
        </div>
      </main>
      
      {/* Career Detail Modal */}
      <CareerDetailModal 
        career={selectedCareerForModal}
        isOpen={!!selectedCareerForModal}
        onClose={() => setSelectedCareerForModal(null)}
      />
      
      <Footer />
    </div>
  );
}
